import React, { FC, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import CircleCheckFill from '@gravity-ui/icons/CircleCheckFill';
import PencilToLine from '@gravity-ui/icons/PencilToLine';
import { Button, DropdownMenu, Icon, Text } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { useIsDraft } from 'client/hooks/useIsDraft';
import { routes } from 'client/lib/routes';
import { DraftStatus } from 'client/model/draft';
import { OperationType } from 'client/model/operation';
import { InstallableApp, Skill } from 'client/model/skill';
import { isInstallableApp } from 'client/utils';
import { getRevisionSlug } from 'client/utils/time';
import * as keyset from 'i18n/DraftsStatuses';
import * as revisionKeyset from 'i18n/RevisionStatus';
import moment from 'moment';

import { getDisplayDraftStatus, STATUS_TO_THEME_MAP } from '../OperationStatus/OperationStatus';

import styles from './DeploymentStatus.module.scss';

const t = i18n(keyset);
const tRevision = i18n(revisionKeyset);

interface Props {
    app: InstallableApp | Skill;
}

export const getDraftIcon = (draftStatus: DraftStatus | OperationType) =>
    STATUS_TO_THEME_MAP[draftStatus]?.icon ? (
        <Text color={STATUS_TO_THEME_MAP[draftStatus]?.color}>{STATUS_TO_THEME_MAP[draftStatus]?.icon}</Text>
    ) : (
        <Icon data={PencilToLine} />
    );

const DisplayVersion: FC<Props> = ({ app }) => {
    if (!isInstallableApp(app)) {
        return null;
    }
    const version = app?.apkInfosWithTargets?.[0]?.versionCode || app?.draft?.apkInfosWithTargets?.[0]?.versionCode;
    const updatedAt = moment(app.updatedAt).format('DD/MM/YY');

    return (
        <div className={styles.row__version}>
            {version}
            {' • '}
            {updatedAt}
        </div>
    );
};

export const DeploymentStatus: FC<Props> = ({ app }) => {
    const { lastOperationType = OperationType.SkillCreated } = useContext(CurrentAliceAppContext)!;
    const { isDraft, setIsDraft } = useIsDraft(app.channel, app.id);

    let publishedCard = null;

    if (app.onAir) {
        publishedCard = (
            <Button
                className={clx(styles.statusContainer, !isDraft && styles.statusContainer_activeRoute)}
                onClick={() => setIsDraft(false, app.channel)}
                view="flat"
            >
                <div className={styles.row__content}>
                    <Icon className={styles.fillGreen} data={CircleCheckFill} />
                    <span className={styles.publishedLabel__short}>{t('published-short')}</span>
                    <span className={styles.publishedLabel__long}>{t('published-long')}</span>
                </div>
                <DisplayVersion app={app} />
            </Button>
        );
    }

    return (
        <div className={styles.container}>
            {publishedCard}
            <Button
                className={clx(styles.statusContainer, isDraft && styles.statusContainer_activeRoute)}
                onClick={() => setIsDraft(true, app.channel)}
                view="flat"
            >
                <div className={styles.row__content}>
                    {getDraftIcon(getDisplayDraftStatus(true, app.onAir, app.draft.status, lastOperationType))}
                    {t('draft')}
                </div>
            </Button>
        </div>
    );
};

export const DeploymentStatusMobile: FC<Props> = ({ app }) => {
    const { lastOperationType = OperationType.SkillCreated } = useContext(CurrentAliceAppContext)!;
    const { isDraft, setIsDraft } = useIsDraft(app.channel, app.id);

    const navigate = useNavigate();

    const RenderOptions = {
        RenderOnAir: (
            <Button
                className={styles.mobileSelectRow}
                view="flat"
                onClick={() => setIsDraft(false, app.channel)}
                disabled={!app.onAir}
                width="max"
            >
                <Icon className={styles.fillGreen} data={CircleCheckFill} />
                {t('published-short')}
            </Button>
        ),

        RenderDraft: (
            <Button
                className={styles.mobileSelectRow}
                view="flat"
                onClick={() => setIsDraft(true, app.channel)}
                width="max"
            >
                <div className={styles.mobileSelectRow}>
                    {getDraftIcon(getDisplayDraftStatus(true, app.onAir, app.draft.status, lastOperationType))}
                    {t('draft')}
                </div>
            </Button>
        ),
    };

    const handleNavigateToRevision = (revision: InstallableApp['revisions'][number]) => {
        navigate(routes.app.installableAppRevision(app.channel, app.id, getRevisionSlug(revision)));
    };

    let revisionOptions: {
        text: string;
        action: () => void;
    }[] = [];
    if (isInstallableApp(app)) {
        revisionOptions = app.revisions
            .filter((revision) => !revision.isActive)
            .map((revision) => ({
                text: `${tRevision('revision-from')} ${moment(revision.publishedAt).format('DD MMM YYYY, HH:mm')}`,
                action: () => handleNavigateToRevision(revision),
            }));
    }

    if (!app.channel || !app.id) return null;

    return (
        <DropdownMenu
            renderSwitcher={(props) => (
                <div {...props} className={styles.mobileContainer}>
                    {isDraft ? RenderOptions.RenderDraft : RenderOptions.RenderOnAir}
                </div>
            )}
            items={[
                isDraft
                    ? {
                          text: RenderOptions.RenderOnAir,
                          action: () => app.onAir && setIsDraft(false, app.channel),
                      }
                    : {
                          text: RenderOptions.RenderDraft,
                          action: () => setIsDraft(true, app.channel),
                      },
                ...revisionOptions,
            ]}
        />
    );
};
